import React, { useState, useEffect } from "react";
import ToggleSwitch from "../../common/controls/ToggleSwitch/ToggleSwitch";
import FieldBuilder from "../../shared/utils/FieldBuilder";
import ResponsiveRow from "../base/ResponsiveRow";
import SelectControl from "../../common/controls/Select";
import getAlertChannels from "../../../services/alertChannelService";

function AlertChannelForm({ data, setValue, disabled }) {
  const [alertChannels, setAlertChannels] = useState();
  const [filteredChannels, setFilteredChannels] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchAlertChannels = async () => {
      const { data: alertChannelList } = await getAlertChannels();
      if (!cancelled) setAlertChannels(alertChannelList);
    };
    fetchAlertChannels();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!alertChannels) return;
    const conditions = {
      is_enabled: true,
      is_allowed_for_store: true,
      is_production: data.in_production,
      is_simulation: data.in_simulation,
    };

    const filtered = alertChannels.filter((alertChannel) => {
      return Object.entries(conditions).every(([key, value]) => {
        return alertChannel[key] === value;
      });
    });

    if (filtered.length === 0) {
      filtered.push({ id: 0, name: "Not allocated" });
    }
    setFilteredChannels(filtered);
    // Find the first channel with is_default = true
    const defaultChannel = filtered.find((alertChannel) => alertChannel.is_default && alertChannel.id !== 0);

    // If we found a default channel and no alert_channel is currently set, set it as default
    if (defaultChannel && (!data.alert_channel || data.alert_channel === 0 || data.alert_channel === "0")) {
      setValue("alert_channel", defaultChannel.id);
    }
  }, [alertChannels, data.in_production, data.in_simulation]);

  const handleToggleChange = (name, value) => {
    setValue(name, value);
  };

  return (
    <div
      className={`px-3 py-2 mb-2 rounded ${
        disabled
          ? "border-2 border-gray-400 text-neutral-500 opacity-75"
          : !data.in_production && data.in_simulation
          ? "border-2 border-yellow-800 text-yellow-800"
          : !data.in_production
          ? " border-2 border-red-800 text-neutral-700"
          : "border-2 border-gray-500 text-neutral-700"
      } ${disabled ? "pointer-events-none" : ""}`}
    >
      {disabled && (
        <div className="text-muted small mb-2">Alert routing is disabled when the store is inactive (Salesforce).</div>
      )}
      <ResponsiveRow
        header="Alert Routing"
        bottomSeparator={false}
        fields={[
          new FieldBuilder()
            .setId("in_production")
            .setName("in_production")
            .setComponent(
              <ToggleSwitch
                name="in_production"
                id="in_production"
                checked={data.in_production}
                onChange={() => handleToggleChange("in_production", !data.in_production)}
                label="In Production"
                disabled={disabled}
              />
            )
            .build(),
          new FieldBuilder()
            .setId("in_simulation")
            .setName("in_simulation")
            .setComponent(
              <ToggleSwitch
                name="in_simulation"
                id="in_simulation"
                checked={data.in_simulation}
                onChange={() => handleToggleChange("in_simulation", !data.in_simulation)}
                label="In Simulation"
                disabled={disabled}
              />
            )
            .build(),
          new FieldBuilder()
            .setId("alert_channel")
            .setName("alert_channel")
            .setParentTagClassName(parseInt(data.alert_channel ?? 0) === 0 ? " red-critical pl-md-1" : "pl-md-1")
            .setComponent(
              <SelectControl
                name="alert_channel"
                label="Alert Channel"
                options={filteredChannels}
                onChange={({ currentTarget }) => setValue("alert_channel", currentTarget.value)}
                value={data.alert_channel}
                disabled={filteredChannels.length === 1 || disabled}
              />
            )
            .build(),
        ]}
      />
    </div>
  );
}

export default AlertChannelForm;
